import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useMutation, useQuery } from "@apollo/client";
import { GET_CONTRACT, REGENERATE_CONTRACT_ASSETS } from "../../graphql/core";
import ScreenCard, { ButtonType } from "../ScreenCard";
import { useI18n } from "../../hooks/i18n-hooks";
import { ContractSignatureStatus } from "../../__generated__/graphql";
import VStack from "../../ui_atoms/VStack";
import { TitleMediumText } from "../../components/FormattedText";
import { CreateMissionStackParamList } from "../../navigations/CreateMissionStack";
import { useIntercom } from "../../hooks/intercom";
import { useDisableBackButton } from "../../hooks/back-handler";
import { BubbleTalk, BubbleTalkPosition } from "../../components/BubbleTalk";
import DocuIllSvg from "../../assets/images/docu_ill.svg";
import Avatar1Svg from "../../assets/images/avatar_1.svg";
import { kycGradient } from "../../configs/linear-gradients";
import { useTheme } from "../../hooks/use-theme";
import { useStatusBarTranslucent } from "../../hooks/use-status-bar-translucent";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Loading from "../../components/Loading";
import MarkdownView from "../../components/MarkdownView";
import { View } from "react-native";
import { useSnackbar } from "../../hooks/snackbar";

type PreSignScreenProps = StackScreenProps<
    CreateMissionStackParamList & RootStackParamList,
    "CreateMissionPreSign"
>;

/**
 * This screen is used to check if the contract is ready to be signed
 * [ButtonsConfig, labelText, infoText]
 */
const ERRONEOUS_VALUE: [ButtonsConfig, BubbleTalkProps?, string?] = [
    { canClose: true },
    undefined,
    "screens.pre_sign.invalid_info",
];

type BubbleTalkProps = {
    message: string;
    image: React.ComponentType;
    position: BubbleTalkPosition;
};

type ButtonsConfig = {
    canSign?: boolean;
    canClose?: boolean;
};

const STATUS_BUTTON_MAP: Record<
    ContractSignatureStatus | "none",
    [ButtonsConfig?, BubbleTalkProps?, string?]
> = {
    none: [],
    canceled_by_lse: ERRONEOUS_VALUE,
    canceled_by_other: ERRONEOUS_VALUE,
    canceled_by_self: ERRONEOUS_VALUE,
    failed: ERRONEOUS_VALUE,
    need_other_sign: [{ canClose: true }, undefined, "screens.pre_sign.other_sign_info"],
    need_seal: ERRONEOUS_VALUE,
    need_self_sign: [
        { canSign: true, canClose: true },
        { message: "screens.pre_sign.label_ready", image: Avatar1Svg, position: "bottom-right" },
        "screens.pre_sign.valid_info",
    ],
    waiting_self_sign: [
        { canSign: true, canClose: true },
        { message: "screens.pre_sign.label_ready", image: Avatar1Svg, position: "bottom-right" },
        "screens.pre_sign.valid_info",
    ],
    success: [{ canClose: true }, undefined, "screens.pre_sign.invalid_info"],
    waiting_contract: [
        undefined,
        { message: "screens.pre_sign.label_waiting", image: DocuIllSvg, position: "top-left" },
        "screens.pre_sign.valid_info",
    ],
};

const CreateMissionPreSign: React.FC<PreSignScreenProps> = ({ route, navigation }) => {
    const timeoutRef = useRef<NodeJS.Timeout | null>(null); // handle timeout for asset generation
    const { presentMessageComposer } = useIntercom();
    const { colors, marginSize } = useTheme();
    const insets = useSafeAreaInsets();

    const i18n = useI18n();
    const [skip, setSkip] = useState(false);
    const [hasTimeout, setHasTimeout] = useState(false);
    const { showSnackbar } = useSnackbar();
    const { data, error, loading } = useQuery(GET_CONTRACT, {
        variables: { id: route.params.contractId },
        pollInterval: skip ? 0 : 5000, // 5 seconds, waiting for the contract to become ready
    });

    const [doRegenerateContracts] = useMutation(REGENERATE_CONTRACT_ASSETS, {
        variables: { contractId: route.params.contractId },
    });

    const onTimeout = useCallback(() => {
        setSkip(true);
        setHasTimeout(true);
    }, []);

    const stopTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    /**
     * Timeout is started when the screen is mounted and assets regeneration is requested
     * This will handle problem during doppio ( html -> PDF ) asset generation, we will
     * be able to re-generate the assets...
     */
    const startTimeout = useCallback(() => {
        stopTimeout();
        timeoutRef.current = setTimeout(
            () => {
                onTimeout();
            },
            1000 * 60 * 2,
        ); // 2 minutes
    }, [onTimeout, stopTimeout]);

    const onRegenerate = useCallback(() => {
        const _func = async () => {
            stopTimeout();
            setHasTimeout(false);
            try {
                await doRegenerateContracts();
            } catch (e) {
                showSnackbar({
                    mode: "error",
                    text: i18n.t("screens.pre_sign.error"),
                });
            } finally {
                setSkip(false);
                startTimeout();
            }
        };
        void _func();
    }, [stopTimeout, doRegenerateContracts, showSnackbar, i18n, startTimeout]);

    const onContactSupport = useCallback(() => {
        void presentMessageComposer();
        navigation.reset({
            index: 0,
            routes: [{ name: "Home" }],
        });
    }, [presentMessageComposer, navigation]);
    /**
     * react on signature status change
     */
    useEffect(() => {
        setSkip(() => {
            if (!data) {
                return false;
            }

            const newSkip =
                data?.getContract?.signatureStatus !== ContractSignatureStatus.WaitingContract;
            if (newSkip) {
                // stop timeout, the contract is ready or is in error
                stopTimeout();
            }
            return newSkip;
        });
    }, [data, stopTimeout]);

    /**
     * Handle component mount and unmount
     */
    useEffect(() => {
        startTimeout();
        return () => {
            stopTimeout();
        };
    }, [startTimeout, stopTimeout]);

    const signatureStatus = data?.getContract?.signatureStatus;

    const [buttonsConfig, bubbleTalkData, infoText] = STATUS_BUTTON_MAP[signatureStatus || "none"];

    const signNow = useCallback(() => {
        navigation.navigate("ContractPreviewScreen", {
            contractId: route.params.contractId,
        });
    }, [navigation, route.params.contractId]);

    const close = useCallback(() => {
        navigation.reset({
            index: 0,
            routes: [{ name: "Home" }],
        });
    }, [navigation]);

    // disable hardware back button
    useDisableBackButton();

    useStatusBarTranslucent();

    useEffect(() => {
        if (buttonsConfig?.canSign) {
            // When ready to sign, navigate to the contract preview screen
            navigation.popTo("ContractPreviewScreen", {
                contractId: route.params.contractId,
            });
        }
    }, [buttonsConfig?.canSign, navigation, route.params.contractId]);

    const buttons = useMemo<ButtonType[]>(() => {
        return !hasTimeout
            ? [
                  ...(buttonsConfig?.canSign
                      ? [
                            {
                                title: i18n.t("screens.pre_sign.read_and_sign_contract"),
                                mode: "secondary" as const,
                                validating: loading,
                                onPress: signNow,
                            },
                        ]
                      : []),
                  ...(buttonsConfig?.canClose
                      ? [
                            {
                                title: i18n.t("close"),
                                mode: "outlined" as const,
                                onPress: close,
                            },
                        ]
                      : []),
              ]
            : [
                  {
                      title: i18n.t("screens.pre_sign.timeout_retry"),
                      mode: "outlined" as const,
                      onPress: onRegenerate,
                  },
                  {
                      title: i18n.t("screens.pre_sign.timeout_contact"),
                      mode: "secondary" as const,
                      onPress: onContactSupport,
                  },
              ];
    }, [
        buttonsConfig?.canClose,
        buttonsConfig?.canSign,
        close,
        hasTimeout,
        i18n,
        loading,
        onContactSupport,
        onRegenerate,
        signNow,
    ]);
    return (
        <ScreenCard
            buttons={buttons}
            headband={
                error || hasTimeout
                    ? {
                          text: hasTimeout
                              ? i18n.t("screens.pre_sign.timeout")
                              : i18n.t("screens.pre_sign.error"),
                          mode: "error",
                      }
                    : undefined
            }
            style={{ paddingTop: insets.top }}
            isContentContainerFlex
            backgroundGradient={kycGradient}
        >
            <VStack h="2/3" v={1} style={{ flex: 1 }}>
                {bubbleTalkData && (
                    <BubbleTalk
                        personImageComponent={<bubbleTalkData.image />}
                        position={bubbleTalkData.position}
                        bubbleBackgroundColor={colors.background}
                    >
                        <TitleMediumText>{i18n.t(bubbleTalkData.message)}</TitleMediumText>
                    </BubbleTalk>
                )}
                <View style={{ flex: 1, justifyContent: "space-evenly" }}>
                    {signatureStatus === ContractSignatureStatus.WaitingContract && <Loading />}
                    {infoText && (
                        <MarkdownView
                            markdownStyle={{
                                textgroup: { textAlign: "center", marginTop: marginSize },
                            }}
                            markdown={i18n.t(infoText)}
                        />
                    )}
                </View>
            </VStack>
        </ScreenCard>
    );
};

export default CreateMissionPreSign;
